'use client'

import { useEffect, useRef } from 'react'

/**
 * 노출(show) 트래킹 — 요소가 뷰포트에 threshold 이상 보이면 콜백을 1회만 실행.
 * show 이벤트는 "화면 진입 당 1회"라 firedRef 로 중복 적재를 막는다.
 * (상세 useModoocarImpression / 랭킹 useRankImpression 과 동일 패턴)
 */
export const useHomeImpression = <T extends HTMLElement = HTMLDivElement>(
  onImpress: () => void,
  options?: { enabled?: boolean; threshold?: number }
) => {
  const ref = useRef<T | null>(null)
  const firedRef = useRef(false)
  const callbackRef = useRef(onImpress)
  callbackRef.current = onImpress

  const enabled = options?.enabled ?? true
  const threshold = options?.threshold ?? 0.5

  useEffect(() => {
    if (!enabled || firedRef.current || !ref.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !firedRef.current) {
          firedRef.current = true
          callbackRef.current()
          observer.disconnect()
        }
      },
      { threshold }
    )

    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [enabled, threshold])

  return ref
}
